import type { TOnboardingState } from "@/shared/services";
import { ONBOARDING_EVENTS, track } from "@features/analytics";
import type { IPlatformTask } from "./checklist";

const STORAGE_PREFIX = "sd:onboarding-reported:";
const DAY_MS = 24 * 60 * 60 * 1000;

const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`;

const readReported = (userId: string): Set<string> => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(storageKey(userId)) ?? "[]",
    );
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeReported = (userId: string, reported: Set<string>) => {
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify(Array.from(reported)),
    );
  } catch {
    // Best-effort; at worst a task is reported again on the next read.
  }
};

const daysBetween = (from?: string, to?: string | null) => {
  if (!from || !to) return undefined;
  return Math.max(0, Math.floor((Date.parse(to) - Date.parse(from)) / DAY_MS));
};

/**
 * Tasks of the user's list that the server reports completed and this browser has not
 * reported yet, oldest first, each with the progress count it reached.
 */
const findUnreportedTasks = (
  serverTasks: TOnboardingState["tasks"],
  tasks: IPlatformTask[],
  reported: Set<string>,
) => {
  const done = tasks.filter((task) => task.key in serverTasks);
  const fresh = done
    .filter((task) => !reported.has(task.key))
    .sort(
      (a, b) =>
        Date.parse(serverTasks[a.key].completedAt ?? "") -
        Date.parse(serverTasks[b.key].completedAt ?? ""),
    );
  const before = done.length - fresh.length;
  return fresh.map((task, i) => ({ task, completedCount: before + i + 1 }));
};

/**
 * Most tasks are completed by backend hooks (publish, share, invite, API…) while the user
 * is somewhere the checklist isn't mounted, so completion is reported from the server's
 * snapshot rather than from the click. A per-user set in localStorage keeps each task to
 * one event per browser; reports count unique users, so a second device is harmless.
 */
export const reportCompletedTasks = ({
  userId,
  serverTasks,
  tasks,
  taskSet,
  userCreatedAt,
}: {
  userId: string;
  serverTasks: TOnboardingState["tasks"];
  tasks: IPlatformTask[];
  taskSet: string | null;
  userCreatedAt?: string;
}) => {
  const reported = readReported(userId);
  const unreported = findUnreportedTasks(serverTasks, tasks, reported);
  if (!unreported.length) return;
  for (const { task, completedCount } of unreported) {
    const record = serverTasks[task.key];
    track(ONBOARDING_EVENTS.COMPLETE_ONBOARDING_TASK, {
      task: task.key,
      "task group": task.group,
      "completion source": record.source,
      verified: record.verified,
      "completed count": completedCount,
      "total tasks": tasks.length,
      "task set": taskSet,
      "days since signup": daysBetween(userCreatedAt, record.completedAt),
    });
    reported.add(task.key);
  }
  writeReported(userId, reported);
};
